import React, { useState, useContext, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Images
import default_profile_img from "assets/images/default-profile.png";

import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { uploadImage, deleteProfileImage } from "../../../../../shared/api/userInfo";
import { describeApiError } from "../../../../../shared/api/apiError";

const HiddenInput = styled("input")({
  display: "none",
});

function Header({ profile }: { profile: any }): JSX.Element {
  const [imageSrc, setImageSrc] = useState<string>(default_profile_img);
  const fileInputRef = useRef<globalThis.HTMLInputElement | null>(null);

  const { session, userInfo, setUserInfo } = useSessionContext();
  const { showNotification } = useNotification();
  const { t } = useContext(LanguageContext) as any;

  useEffect(() => {
    setImageSrc(profile?.profile_image_url || default_profile_img);
  }, [profile]);

  const handleFileChange = async (event: React.ChangeEvent<globalThis.HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Sttting-Header.size-error"),
        },
        3000
      );
      return;
    }
    const imagePreviewUrl = URL.createObjectURL(file);
    setImageSrc(imagePreviewUrl);
    try {
      const res = (await uploadImage(session.access_token, file)) as { profile_image_url?: string } | undefined;
      // サイドバーなど他の画面は共有のユーザー情報を表示するため、新しい画像URLを反映する。
      // 反映しないと、再ログインするまで古い画像のままになる。
      if (res?.profile_image_url && userInfo) {
        setUserInfo({ ...userInfo, profile_image_url: res.profile_image_url });
      }
      showNotification(
        {
          icon: "info",
          title: t("Common.success"),
          message: t("Common.updated-success"),
        },
        3000
      );
    } catch (error) {
      console.log(`Error handleFileChange:${error}`);
      setImageSrc(profile?.profile_image_url || default_profile_img);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          // 失敗の実際の理由（HTTPの状態、サーバーのメッセージ）を添える。
          message: `${t("Common.updated-error")} (${describeApiError(error).text})`,
        },
        3000
      );
    }
  };

  const handleImageDelete = async () => {
    if (!window.confirm(t("Sttting-Header.delete-confirm"))) return;
    try {
      await deleteProfileImage(session.access_token);
      // 共有のユーザー情報も更新し、サイドバーなどもデフォルト画像に戻す。
      if (userInfo) setUserInfo({ ...userInfo, profile_image_url: undefined });
      setImageSrc(default_profile_img);
      showNotification(
        { icon: "info", title: t("Common.success"), message: t("Common.delete-success") },
        3000
      );
    } catch (error) {
      console.log(`Error handleImageDelete:${error}`);
      showNotification(
        { icon: "error", title: t("Common.error"), message: t("Common.delete-error") },
        3000
      );
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card id="profile">
      <MDBox p={2}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <MDAvatar src={imageSrc} alt="profile-image" size="xl" shadow="sm" />
            <HiddenInput
              type="file"
              accept="image/*"
              ref={fileInputRef}
              aria-label={t("Sttting-Header.uload-label")}
              onChange={handleFileChange}
            />
          </Grid>
          <Grid item>
            <MDBox height="100%" mt={0.5} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                {profile?.nickname || t("Sttting-Header.non-name")}
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="medium">
                {profile?.title || t("Sttting-Header.non-title")}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3} sx={{ ml: "auto" }}>
            <MDBox
              display="flex"
              justifyContent={{ md: "flex-end" }}
              alignItems="center"
              lineHeight={1}
            >
              <MDButton variant="gradient" color="info" size="small" onClick={handleImageButtonClick}>
                {t("Sttting-Header.label1")}
              </MDButton>
              {profile?.profile_image_url && (
                <MDBox ml={1}>
                  <MDButton variant="outlined" color="error" size="small" onClick={handleImageDelete}>
                    {t("Sttting-Header.delete-label")}
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default Header;
